import React, { useState } from 'react';
import { Heart, CheckCircle, Briefcase } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Careers = () => {
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', availability: '', experience: '', honeypot: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/submissions/career`, formData);
      if (res.data.success) {
        toast.success('Thank you for your application. We will be in touch shortly.');
        setFormData({ name: '', email: '', phone: '', availability: '', experience: '', honeypot: '' });
      }
    } catch (error) {
      if (error.response?.status === 429) toast.error('Too many submissions. Please try again later.');
      else toast.error('Something went wrong, please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white">
      <section className="bg-care-blue text-white py-20">
        <div className="max-w-7xl mx-auto px-6">
          <p className="text-care-green font-semibold text-sm mb-2 tracking-wide">CAREERS</p>
          <h1 className="text-4xl md:text-5xl font-bold mb-6">Join PrimeShift as a PCA</h1>
          <p className="text-xl text-white/80 max-w-3xl leading-relaxed">
            Are you a compassionate and experienced Personal Care Assistant? Join our network and find permanent positions in quality aged care homes across Sydney.
          </p>
        </div>
      </section>

      <section className="py-16 bg-care-light">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-care-dark mb-6">Why Join PrimeShift?</h2>
              <div className="space-y-4">
                {[
                  'Permanent positions in reputable aged care facilities',
                  'Competitive pay and stable employment',
                  'Supportive placement process from start to finish',
                  'Opportunities across the Sydney metropolitan area',
                  'Work with residents who value your compassion',
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-care-green flex-shrink-0 mt-0.5" />
                    <p className="text-gray-700">{item}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-lg overflow-hidden h-72">
              <img
                src="https://images.pexels.com/photos/7551622/pexels-photo-7551622.jpeg?w=800&auto=compress"
                alt="PCA caring for elderly resident"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-care-dark mb-4">Apply Now</h2>
            <p className="text-lg text-gray-600">Submit your details and we will match you with suitable positions.</p>
          </div>
          <Card className="shadow-xl border-none">
            <CardHeader className="bg-care-blue text-white rounded-t-lg">
              <CardTitle className="text-2xl">PCA Application Form</CardTitle>
            </CardHeader>
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <input type="text" name="honeypot" value={formData.honeypot} onChange={handleChange} style={{ display: 'none' }} tabIndex="-1" autoComplete="off" />
                <div>
                  <Label htmlFor="name" className="text-base">Full Name *</Label>
                  <Input id="name" name="name" value={formData.name} onChange={handleChange} required className="mt-2 h-12" placeholder="Your full name" data-testid="career-name-input" />
                </div>
                <div>
                  <Label htmlFor="email" className="text-base">Email *</Label>
                  <Input id="email" name="email" type="email" value={formData.email} onChange={handleChange} required className="mt-2 h-12" placeholder="your.email@example.com" data-testid="career-email-input" />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-base">Phone *</Label>
                  <Input id="phone" name="phone" type="tel" value={formData.phone} onChange={handleChange} required className="mt-2 h-12" placeholder="Contact number" data-testid="career-phone-input" />
                </div>
                <div>
                  <Label htmlFor="availability" className="text-base">Availability *</Label>
                  <Input id="availability" name="availability" value={formData.availability} onChange={handleChange} required className="mt-2 h-12" placeholder="e.g. Full-time, available immediately" data-testid="career-availability-input" />
                </div>
                <div>
                  <Label htmlFor="experience" className="text-base">Experience and Qualifications *</Label>
                  <Textarea id="experience" name="experience" value={formData.experience} onChange={handleChange} required className="mt-2" rows={5} placeholder="Describe your aged care experience, certifications (Cert III, First Aid), and any relevant qualifications..." data-testid="career-experience-input" />
                </div>
                <Button type="submit" size="lg" className="w-full bg-care-green hover:bg-care-green-rich text-white h-12 text-base font-semibold transition-colors duration-300" disabled={isSubmitting} data-testid="career-submit-button">
                  {isSubmitting ? 'Submitting...' : 'Submit Application'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default Careers;
